/**
 * In-memory + vault-backed session state for the parent PWA.
 *
 * The family key is the crown jewel: 32 bytes that everyone in the family
 * shares. At rest we store it in IndexedDB *wrapped* under a key derived
 * from the user's password + a per-install random salt (PBKDF2-HMAC-SHA256).
 * On login we PBKDF2 the password with the stored salt to recover the wrap
 * key, then ChaCha20-Poly1305-decrypt the stored blob to recover the family
 * key. On logout we clear the vault and the in-memory copy.
 */

import { computed, signal } from '@preact/signals';
import { chacha20poly1305 } from '@noble/ciphers/chacha.js';
import * as vault from '../e2ee/vault';
import {
  loadFamilyKeyForFirebase,
  storeFamilyKeyForFirebase,
  wipeFirebaseVault,
} from '../e2ee/deviceKey';

/** In-memory copy of the family key, populated after signup/login. */
export const familyKeyInMemory = signal<Uint8Array | null>(null);

/**
 * True once the boot-time attempt to load the family key from this
 * browser's vault has finished, whether or not it found one. Until then a
 * null `familyKeyInMemory` means "still loading", not "missing".
 */
export const familyKeyHydrated = signal(false);

/**
 * Auth session: server-issued tokens + identity. Populated after successful
 * signup or login. Kept in-memory only; refresh flow is deferred to 019+.
 */
export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  parentId: string;
  familyId: string;
  email: string;
}

const SESSION_STORAGE_KEY = 'backstop.session.v1';

function hydrateAuthSession(): AuthSession | null {
  try {
    const raw = window.localStorage.getItem(SESSION_STORAGE_KEY);
    if (raw === null) return null;
    return JSON.parse(raw) as AuthSession;
  } catch {
    return null;
  }
}

export const authSession = signal<AuthSession | null>(hydrateAuthSession());
export const isAuthed = computed(() => authSession.value !== null);

export function setAuthSession(s: AuthSession): void {
  authSession.value = s;
  try {
    window.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(s));
  } catch {
    // localStorage may be disabled; session stays in-memory only
  }
}

export function clearAuthSession(): void {
  authSession.value = null;
  try {
    window.localStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {
    // no-op
  }
}

/**
 * Vault keys and sessionStorage cache keys are namespaced by email so
 * that multiple Backstop accounts can coexist on the same browser
 * without stomping each other's wrapped family keys (plan 031).
 *
 * The client normalises email with `.trim().toLowerCase()` before use
 * so casing differences don't produce distinct slots; matches the CP's
 * CITEXT-backed email uniqueness.
 */
function normEmail(email: string): string {
  return email.trim().toLowerCase();
}

const WRAPPED_KEY_PREFIX = 'wrapped_family_key';
const SALT_KEY_PREFIX = 'client_salt';

function wrappedKey(email: string): string {
  return `${WRAPPED_KEY_PREFIX}:${normEmail(email)}`;
}
function saltKey(email: string): string {
  return `${SALT_KEY_PREFIX}:${normEmail(email)}`;
}

const VAULT_AAD = new TextEncoder().encode('vault-v1');
const PBKDF2_ITERATIONS = 210_000; // OWASP 2023 recommendation for SHA-256
const SALT_LENGTH = 16;

/**
 * "Remember" cache — the 32-byte wrap key derived from the user's password +
 * salt, stashed in sessionStorage under an explicit expiry. Deliberately
 * sessionStorage, not localStorage: closing the tab auto-clears it, so the
 * user can log out by closing the window.
 *
 * The wrap key alone can't decrypt anything without the wrapped-family-key
 * blob + salt from the IndexedDB vault; we still need that vault entry on
 * rehydrate. The remember cache just spares the user from re-entering the
 * password to re-derive the wrap key every reload.
 *
 * See `docs/debt/004-family-key-rehydration.md`.
 */
const WRAP_KEY_CACHE_KEY_PREFIX = 'backstop.wrap-key.v1';
const WRAP_KEY_EXPIRES_AT_PREFIX = 'backstop.wrap-key-expires-at';

function wrapKeyCacheKey(email: string): string {
  return `${WRAP_KEY_CACHE_KEY_PREFIX}:${normEmail(email)}`;
}
function wrapKeyExpiresAtKey(email: string): string {
  return `${WRAP_KEY_EXPIRES_AT_PREFIX}:${normEmail(email)}`;
}

const REMEMBER_DURATION_MS = 12 * 60 * 60 * 1000; // 12 hours

function uint8ToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

function base64ToUint8(b64: string): Uint8Array {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

function readCachedWrapKey(email: string): Uint8Array | null {
  try {
    const expiresAt = window.sessionStorage.getItem(wrapKeyExpiresAtKey(email));
    const raw = window.sessionStorage.getItem(wrapKeyCacheKey(email));
    if (expiresAt === null || raw === null) return null;
    if (Date.now() > Number(expiresAt)) {
      clearCachedWrapKey(email);
      return null;
    }
    return base64ToUint8(raw);
  } catch {
    return null;
  }
}

function cacheWrapKey(email: string, wrapKey: Uint8Array): void {
  try {
    window.sessionStorage.setItem(
      wrapKeyCacheKey(email),
      uint8ToBase64(wrapKey),
    );
    window.sessionStorage.setItem(
      wrapKeyExpiresAtKey(email),
      String(Date.now() + REMEMBER_DURATION_MS),
    );
  } catch {
    // sessionStorage may be disabled — remember is best-effort.
  }
}

function clearCachedWrapKey(email: string): void {
  try {
    window.sessionStorage.removeItem(wrapKeyCacheKey(email));
    window.sessionStorage.removeItem(wrapKeyExpiresAtKey(email));
  } catch {
    // no-op
  }
}

/** Derive a 32-byte AEAD key from a password + salt via PBKDF2-HMAC-SHA256. */
async function deriveWrapKey(
  password: string,
  salt: Uint8Array,
): Promise<Uint8Array> {
  const material = await crypto.subtle.importKey(
    'raw',
    new TextEncoder().encode(password),
    'PBKDF2',
    false,
    ['deriveBits'],
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'PBKDF2',
      salt: salt as BufferSource,
      iterations: PBKDF2_ITERATIONS,
      hash: 'SHA-256',
    },
    material,
    256,
  );
  return new Uint8Array(bits);
}

/**
 * Wrap `familyKey` under a key derived from `password`, persist the wrapped
 * blob + salt in IndexedDB under this `email`'s slot, and populate the
 * in-memory signal. Returns the salt (16 random bytes) so callers can log /
 * display / persist elsewhere if needed.
 */
export async function storeFamilyKey(
  email: string,
  password: string,
  familyKey: Uint8Array,
): Promise<Uint8Array> {
  if (familyKey.length !== 32) {
    throw new Error(
      `family key must be 32 bytes, got ${familyKey.length.toString()}`,
    );
  }
  const salt = new Uint8Array(SALT_LENGTH);
  crypto.getRandomValues(salt);
  const wrapKey = await deriveWrapKey(password, salt);
  const nonce = new Uint8Array(12); // deterministic-zero: wrap key is derived per-install and single-use
  const wrapped = chacha20poly1305(wrapKey, nonce, VAULT_AAD).encrypt(
    familyKey,
  );
  await vault.put(wrappedKey(email), wrapped);
  await vault.put(saltKey(email), salt);
  familyKeyInMemory.value = familyKey;
  return salt;
}

/**
 * If a wrapped family key + salt live in the vault for `email`, derive the
 * wrap key from `password`, decrypt, and populate `familyKeyInMemory`.
 * Returns `true` on success, `false` if nothing is stored yet. Throws on
 * wrong password.
 *
 * With `{ remember: true }`, also stashes the derived wrap key in
 * sessionStorage under a 12-hour expiry so subsequent tab reloads within the
 * session can skip the password prompt (see `hydrateFamilyKeyFromCache`).
 */
export async function loadFamilyKey(
  email: string,
  password: string,
  opts?: { remember?: boolean },
): Promise<boolean> {
  const wrapped = await vault.get(wrappedKey(email));
  const salt = await vault.get(saltKey(email));
  if (wrapped === null || salt === null) {
    return false;
  }
  const wrapKey = await deriveWrapKey(password, salt);
  const nonce = new Uint8Array(12);
  const fk = chacha20poly1305(wrapKey, nonce, VAULT_AAD).decrypt(wrapped);
  familyKeyInMemory.value = fk;
  if (opts?.remember === true) {
    cacheWrapKey(email, wrapKey);
  }
  return true;
}

/**
 * Diagnostic shape used by the login and restore screens to render
 * accurate error messages when unlocking doesn't work.
 */
export type UnlockResult =
  /** Vault entries were present and the password decrypted them. */
  | { kind: 'ok' }
  /** No wrapped family key on this device. Fresh browser / cleared data. */
  | { kind: 'empty' }
  /**
   * Wrapped family key present but the password didn't match. Either the
   * user typed a different password than the one they set on this device,
   * or the wrap belongs to a different account previously used in this
   * browser.
   */
  | { kind: 'wrong-password' };

/**
 * Same as `loadFamilyKey`, but returns a discriminated result instead of
 * throwing. Callers can pick a targeted error message.
 */
export async function tryLoadFamilyKey(
  email: string,
  password: string,
  opts?: { remember?: boolean },
): Promise<UnlockResult> {
  const wrapped = await vault.get(wrappedKey(email));
  const salt = await vault.get(saltKey(email));
  if (wrapped === null || salt === null) {
    return { kind: 'empty' };
  }
  try {
    const wrapKey = await deriveWrapKey(password, salt);
    const nonce = new Uint8Array(12);
    const fk = chacha20poly1305(wrapKey, nonce, VAULT_AAD).decrypt(wrapped);
    familyKeyInMemory.value = fk;
    if (opts?.remember === true) {
      cacheWrapKey(email, wrapKey);
    }
    return { kind: 'ok' };
  } catch {
    return { kind: 'wrong-password' };
  }
}

/**
 * On tab reload, if a non-expired wrap key sits in sessionStorage and the
 * vault still holds the wrapped family key, decrypt in place and populate
 * `familyKeyInMemory` — sparing the user from re-entering the password.
 * On any failure (missing vault, stale key, decrypt error), quietly clears
 * the cache so we fall back to the unlock prompt.
 */
async function hydrateFamilyKeyFromCache(email: string): Promise<void> {
  const wrapKey = readCachedWrapKey(email);
  if (wrapKey === null) return;
  try {
    const wrapped = await vault.get(wrappedKey(email));
    if (wrapped === null) {
      clearCachedWrapKey(email);
      return;
    }
    const nonce = new Uint8Array(12);
    const fk = chacha20poly1305(wrapKey, nonce, VAULT_AAD).decrypt(wrapped);
    familyKeyInMemory.value = fk;
  } catch {
    clearCachedWrapKey(email);
  }
}

/**
 * True iff this device has a password-wrapped family key on disk for the
 * given `email`. Used by the Firebase-restore flow to offer "unlock with
 * password" alongside the mnemonic option, so a parent who already set up
 * this browser via password doesn't need to re-enter the phrase just
 * because they came back through Google.
 */
export async function hasPasswordVault(email: string): Promise<boolean> {
  const wrapped = await vault.get(wrappedKey(email));
  const salt = await vault.get(saltKey(email));
  return wrapped !== null && salt !== null;
}

/**
 * Firebase (Google) sign-in path: no password to derive from at boot, so
 * try to load the family key wrapped under the device-local AES-GCM key.
 * Silent no-op on password accounts (the vault entries won't exist).
 * Runs alongside `hydrateFamilyKeyFromCache` — whichever completes first
 * wins; the other becomes a no-op on already-populated in-memory state.
 */
async function hydrateFirebaseFamilyKey(email: string): Promise<void> {
  if (familyKeyInMemory.value !== null) return;
  try {
    const fk = await loadFamilyKeyForFirebase(email);
    if (fk !== null && familyKeyInMemory.value === null) {
      familyKeyInMemory.value = fk;
    }
  } catch {
    // Corrupt vault — leave the signal null so the app treats this as a
    // fresh device and routes the user to restore-with-mnemonic.
  }
}

/**
 * Firebase-signup and mnemonic-recovery entry point: persist the family
 * key under a fresh device-local AES-GCM key + populate the in-memory
 * signal. No password required.
 */
export async function storeFamilyKeyFirebase(
  email: string,
  familyKey: Uint8Array,
): Promise<void> {
  await storeFamilyKeyForFirebase(email, familyKey);
  familyKeyInMemory.value = familyKey;
}

/**
 * Full local wipe of the Firebase-path vault for `email`. Used by account
 * deletion flows. Passwords use `wipeLocalVault` (unrelated).
 */
export async function wipeFirebaseLocalVault(email: string): Promise<void> {
  await wipeFirebaseVault(email);
  familyKeyInMemory.value = null;
}

// Fire on module load. familyKeyInMemory starts null; subscribers will pick
// up the transition once the async decrypt lands. Boot-time hydrate only
// runs when we already have an authSession — otherwise there's no email to
// key vault reads with, and the app is either fresh or freshly signed out.
if (authSession.value !== null) {
  void Promise.allSettled([
    hydrateFamilyKeyFromCache(authSession.value.email),
    hydrateFirebaseFamilyKey(authSession.value.email),
  ]).then(() => {
    familyKeyHydrated.value = true;
  });
} else {
  familyKeyHydrated.value = true;
}

/**
 * Sign out: drop the auth session + in-memory family key + wrap-key cache
 * (for the currently signed-in email), but PRESERVE the wrapped family key
 * in IndexedDB so the next login can re-decrypt it with just the password.
 * Without this the login flow has nothing to unwrap and forces the user
 * through the recovery-with-mnemonic form every time they sign out.
 *
 * Other accounts' wrap-key caches (if a parent has signed into multiple
 * accounts on this browser) are left intact.
 *
 * Callers that need a full local wipe (account deletion, "erase this
 * device") should use [`wipeLocalVault`] instead.
 */
export async function logout(): Promise<void> {
  const currentEmail = authSession.value?.email;
  familyKeyInMemory.value = null;
  if (currentEmail !== undefined) {
    clearCachedWrapKey(currentEmail);
  }
  clearAuthSession();
}

/**
 * Nuclear option for the given `email`: clear its vault entries + wrap-key
 * cache + in-memory state + auth session. Used on account reset and the
 * "Wipe this browser and recover" affordance in LoginRoute. Other accounts'
 * vault slots on this browser are left intact.
 */
export async function wipeLocalVault(email: string): Promise<void> {
  await vault.del(wrappedKey(email));
  await vault.del(saltKey(email));
  familyKeyInMemory.value = null;
  clearCachedWrapKey(email);
  clearAuthSession();
}
